<script lang="ts">
	import Plus from '$lib/assets/images/icons/plus-icon.svg?component';
	import CloseOutline from '$lib/assets/images/icons/x-close.svg?component';
	import Input from '$lib/components/Global/Fields/Input.svelte';
	import Select from '$lib/components/Global/Fields/Select.svelte';
	import __ from '$lib/stores/TranslationStore';
	import { formatStrFloat } from '$lib/utils/helpers/moneyInput';
	import { convertToCurrency } from '$lib/utils/helpers/numbers';
	import { getContext, onMount } from 'svelte';
	import { fade, slide } from 'svelte/transition';
	import { useStepFieldUpdater } from '../../Stepper/stepper';

	export let name: string;

	type Credit = {
		id: symbol;
		type: string;
		debt: number;
		installment: number;
	};

	let creditType: string = '';
	let debt: string | undefined;
	let installment: string | undefined;
	const creditOptions: Record<string, string> = {
		home_credit: $__('Créd. Habitação'),
		personal_credit: $__('Créd. Pessoal'),
		credit_card: $__('Cartão de Créd.'),
		car: $__('Automóvel'),
		others: $__('Outros')
	};

	let credits: Credit[] = [];

	$: canAddCredit = !!creditType && !!debt && !!installment;

	const hash = getContext<symbol>('stepHash');
	const { value, updateValue, markAsChanged, step } = useStepFieldUpdater<Credit[] | undefined>(
		hash,
		name
	);

	markAsChanged();

	function addCredit() {
		const newCredit: Credit = {
			id: Symbol(),
			type: creditType,
			debt: convertToCurrency(String(debt)),
			installment: convertToCurrency(String(installment))
		};

		credits = [...credits, newCredit];
		updateValue(() => ({
			[name]: credits
		}));

		creditType = '';
		debt = undefined;
		installment = undefined;
	}

	function removeCredit(id: symbol) {
		credits = credits?.filter((credit) => credit.id !== id);
		updateValue(() => ({
			[name]: credits
		}));
	}
	$: totalDebt = formatStrFloat(String(credits?.reduce((acc, credit) => acc + credit.debt, 0)));
	$: totalInstallment = formatStrFloat(
		String(credits?.reduce((acc, credit) => acc + credit.installment, 0))
	);

	onMount(() => {
		const unsubscribe = value.subscribe((value) => {
			credits = value || [];
		});
		unsubscribe();
	});
</script>

<section class="flex flex-col gap-8">
	<form
		class="flex w-full flex-col gap-[10px] rounded-xl bg-inactive-light p-3 lg:h-24 lg:flex-row"
	>
		<div class="flex flex-col justify-end">
			<Select
				class="h-10 px-2 py-[5px] lg:min-w-[225px] {creditType === ''
					? 'bg-white'
					: 'bg-secondary-light'}"
				name="credit_type"
				placeholder={$__('Tipo de crédito')}
				bind:value={creditType}
				options={Object.keys(creditOptions).map((key) => ({
					label: creditOptions[key],
					value: key
				}))}
			/>
		</div>
		<div class="flex flex-col justify-end gap-2">
			<label for="debt">{$__('Dívida')}</label>
			<Input
				class="h-10 rounded-xl bg-white px-2 py-[5px]"
				inputClass="py-[2px] text-lg"
				name="debt"
				borderless
				showUpLabel={false}
				placeholder="Ex: 10 000€"
				inputmode="numeric"
				bind:value={debt}
				on:input={() => (debt = formatStrFloat(debt || ''))}
			>
				<div slot="label" />
				<div class="-ml-1 cursor-text rounded-sm bg-secondary text-white" slot="after">
					<p class="m-0 select-none px-[2px] py-[3px] leading-none">€</p>
				</div>
			</Input>
		</div>
		<div class="flex flex-col justify-end gap-2">
			<label for="debt">{$__('Prestação')}</label>
			<Input
				class="h-10 rounded-xl bg-white px-2 py-[5px]"
				inputClass="py-[2px] text-lg"
				name="debt"
				borderless
				showUpLabel={false}
				placeholder="Ex: 10 000€"
				inputmode="numeric"
				bind:value={installment}
				on:input={() => (installment = formatStrFloat(installment || ''))}
			>
				<div slot="label" />
				<div class="-ml-1 cursor-text rounded-sm bg-secondary text-white" slot="after">
					<p class="m-0 select-none px-[2px] py-[3px] leading-none">€</p>
				</div>
			</Input>
		</div>
		<button
			class:bg-primary={canAddCredit}
			class:text-white={canAddCredit}
			disabled={!canAddCredit}
			on:click={addCredit}
			class="mt-4 flex h-[60px] items-center justify-center gap-[12px] rounded-[10px] bg-inactive px-4 py-1 text-lg text-slate-inactive lg:mt-0 lg:h-auto lg:flex-col lg:gap-1 lg:text-xs"
		>
			<Plus
				class="mb-1 h-5 stroke-[4px] lg:mb-0 lg:h-4 lg:stroke-2 {canAddCredit
					? 'stroke-white'
					: 'stroke-slate-inactive'}"
			/>
			{$__('Adicionar')}
		</button>
	</form>
	{#if (credits || []).length > 0}
		<div transition:slide class="overflow-y-auto">
			<table class="w-full border-collapse">
				<thead>
					<tr class="text-left text-xs uppercase text-primary lg:text-sm">
						<th />
						<th>{$__('Tipo')}</th>
						<th>{$__('Dívida')}</th>
						<th>{$__('Prestação')}</th>
						<th />
					</tr>
				</thead>
				<tbody>
					{#each credits || [] as credit, i (credit.id)}
						<tr transition:fade class="h-10 text-left text-xs lg:text-sm">
							<td>
								<span
									class="flex h-[14px] w-[14px] shrink-0 grow-0 items-center justify-center rounded-full border-[0.8px] border-primary p-2 text-[10px] font-bold text-primary lg:h-5 lg:w-5 lg:text-xs"
								>
									{i + 1}
								</span>
							</td>
							<td class="truncate">{creditOptions[credit.type] || $__('Outros')}</td>
							<td>
								<span>{formatStrFloat(String(credit.debt))}</span>
								<span class="cursor-text rounded-sm bg-secondary text-white">
									<span class="m-0 select-none px-[2px] py-[3px] leading-none">€</span>
								</span>
							</td>
							<td>
								<span>{formatStrFloat(String(credit.installment))}</span>
								<span class="cursor-text rounded-sm bg-secondary text-white">
									<span class="m-0 select-none px-[2px] py-[3px] leading-none">€/{$__('mês')}</span>
								</span>
							</td>
							<td>
								<button type="button" on:click={() => removeCredit(credit.id)}>
									<CloseOutline class="h-6 stroke-red-500 stroke-2 lg:h-8" />
								</button>
							</td>
						</tr>
					{/each}
					<tr class="h-12 border-t-[1px] border-inactive text-xs text-dark lg:text-sm">
						<td class="" colspan="2">
                            <span class="font-bold uppercase">{$__('Total')}</span> 
                            {#if parseFloat((totalDebt.replace(" ",""))) < 5000 }
                                <span class="text-xs text-[#FF0000]">{$__('(valor mínimo de 5 000€)')} </span>
                            {/if}
                        </td>
						<td class="font-semibold">{totalDebt} €</td>
						<td class="font-semibold">{totalInstallment} €</td>
						<td />
					</tr>
				</tbody>
			</table>
		</div>
	{/if}
</section>
